import axiosInstance from './axiosInstance';

/**
 * Auth Service
 * Handles all authentication related API calls
 */

const authService = {
  /**
   * Login with user_id and pin
   * @param {string} user_id
   * @param {string} pin
   * @returns {Promise}
   */
  login: async (user_id, pin) => {
    try {
      const response = await axiosInstance.post('/admin/auth/login', {
        user_id,
        pin,
      });
      
      // Save tokens and user data to localStorage
      if (response.data.success) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Refresh access token
   * @returns {Promise}
   */
  refreshAccessToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axiosInstance.post('/admin/auth/refresh', {
        refreshToken,
      });

      if (response.data.success) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
      }

      return response.data;
    } catch (error) {
      // Clear auth data on refresh failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw error.response?.data || error;
    }
  },

  /**
   * Is the mobile app switched on for the signed-in manager's own account?
   *
   * Managers get no app login by role — they enable it for themselves, usually
   * to demo the app to their carers. Managers only: the API refuses anyone else,
   * so a staff member cannot grant themselves what their manager withheld.
   */
  getMyAppAccess: async () => {
    try {
      const response = await axiosInstance.get('/carehome-manager/auth/me/app-access');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Switch the mobile app on or off for the signed-in manager's own account.
   * @param {boolean} appAccess
   */
  updateMyAppAccess: async (appAccess) => {
    try {
      const response = await axiosInstance.patch('/carehome-manager/auth/me/app-access', {
        app_access: appAccess,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Multi-site groups: the sites the signed-in user may open, and which one
   * their tokens are for. One site (or none) means there is no switcher.
   * @returns {Promise<{success, data: {sites: {id, name}[], active_site_id}}>}
   */
  getMySites: async () => {
    try {
      const response = await axiosInstance.get('/carehome-manager/sites');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Open another site. The API re-issues both tokens for it, so every request
   * after this one is about that site; the new tokens and user are stored here.
   * @param {number} siteId
   */
  switchSite: async (siteId) => {
    try {
      const response = await axiosInstance.post('/carehome-manager/sites/switch', { site_id: siteId });
      if (response.data.success) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Logout - clear local storage
   */
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  /**
   * Get stored user from localStorage
   * @returns {Object|null}
   */
  getStoredUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('accessToken');
  },
};

export default authService;
